import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import socket from '../utils/socket';
import { deriveOrderFlags } from '../utils/wipOrderLogic';
import { SkelTable } from './Skeletons';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const LIMIT = 1000;
const PAGE_SIZE = 50;

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtWhen = (at) => { try { return new Date(at).toLocaleString(); } catch { return ''; } };
const fmtDate = (d) => { if (!d) return ''; try { return new Date(d).toLocaleDateString([], { day: '2-digit', month: 'short', year: '2-digit' }); } catch { return String(d); } };
const daysBetween = (a, b) => { if (!a || !b) return null; const d1 = new Date(a), d2 = new Date(b); if (isNaN(d1) || isNaN(d2)) return null; return Math.round((d2 - d1) / 86400000); };

// Load the sidebar logo as a PNG data-URL for embedding in the exported PDF.
async function loadLogoDataUrl() {
  try {
    const img = new Image(); img.crossOrigin = 'anonymous'; img.src = '/vetobase-icon.png';
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const c = document.createElement('canvas'); c.width = img.naturalWidth || 64; c.height = img.naturalHeight || 64;
    c.getContext('2d').drawImage(img, 0, 0); return c.toDataURL('image/png');
  } catch { return null; }
}

const EMPTY_FILTERS = { brand: '', confirmationStatus: '', article: '', collection: '', category: '', stitchCategory: '', status: '', stitchingTeam: '', fulfillmentStatus: '', deliveryStatus: '', refundStatus: '', domain: '', delay: '', orderFrom: '', orderTo: '', noIssuance: '', noReceiving: '', noOgp: '', noIgp: '' };
// "delay" filter value → the deriveOrderFlags key for that stage.
const DELAY_FLAG = { issuance: 'delayedIssuanceStage', receiving: 'delayedReceiving', dispatch: 'delayedIssuance', delivery: 'delayedDelivery' };
const DELAY_LABEL = { issuance: 'Late issuance', receiving: 'Late receiving', dispatch: 'Late dispatch', delivery: 'Late delivery' };

/**
 * The merged Order-Tracking board — raw Shopify order data + editable production
 * tracking in one grid. Grouped/sticky headers, all dates in one sequence, SLA
 * red-flagging, a Filters popup, pagination, an Issued-By people manager, live
 * PostEx delivery sync and a branded PDF export. Edits save inline with NO page
 * reload — the row updates optimistically and live socket events merge in place.
 */
export default function OrdersBoard({ workspaceUuid, shopDomain, brandName }) {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [people, setPeople] = useState([]);   // stitching-team names (managed)
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);      // {id, key}
  const [draft, setDraft] = useState('');
  const [history, setHistory] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [managePeople, setManagePeople] = useState(false);
  const [timelinesOpen, setTimelinesOpen] = useState(false);
  const [timelines, setTimelines] = useState(null);       // { unstitched:{...}, stitched:{...} }
  const [savingTimelines, setSavingTimelines] = useState(false);
  // Bulk multi-select + bulk edit
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const selAllRef = useRef(null);
  const [newPerson, setNewPerson] = useState('');
  const editRef = useRef(null);

  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState(EMPTY_FILTERS);   // active filters
  const [draftF, setDraftF] = useState(EMPTY_FILTERS);      // filters being edited in the popup
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);

  const base = `${API_URL}/api/wip/orders`;
  const storeQS = shopDomain ? `&shopDomain=${encodeURIComponent(shopDomain)}` : '';

  // Full (re)load. Used on mount / store switch / explicit Refresh only — NOT on edits.
  const load = useCallback(async (spinner = true) => {
    if (spinner) setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${base}?workspaceUuid=${workspaceUuid}&limit=${LIMIT}${storeQS}`, auth());
      setRows(r.data.items || []);
      setColumns(r.data.columns || []);
      setPeople(r.data.people || []);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not load orders.');
    } finally { if (spinner) setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid, shopDomain]);

  useEffect(() => { load(); }, [load]);

  // Mark the board as seen when opened — clears the Orders unread badge in the navbar.
  useEffect(() => {
    if (!workspaceUuid) return;
    axios.post(`${base}/mark-seen`, { workspaceUuid }, auth()).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid]);

  // Live updates — merge a single record in place (no full reload / no scroll jump).
  useEffect(() => {
    if (!workspaceUuid) return;
    socket.emit('joinWorkspace', workspaceUuid);
    const onUpsert = (p) => {
      if (p?.board !== 'orders' || !p.record) return;
      setRows(prev => {
        const i = prev.findIndex(r => String(r._id) === String(p.record._id));
        if (i === -1) return [p.record, ...prev];
        const next = [...prev]; next[i] = p.record; return next;
      });
    };
    const onDelete = (p) => { if (p?.board === 'orders') setRows(prev => prev.filter(r => String(r._id) !== String(p.id))); };
    socket.on('wip:created', onUpsert); socket.on('wip:updated', onUpsert); socket.on('wip:deleted', onDelete);
    return () => { socket.off('wip:created', onUpsert); socket.off('wip:updated', onUpsert); socket.off('wip:deleted', onDelete); };
  }, [workspaceUuid]);

  useEffect(() => { if (editing && editRef.current) editRef.current.focus(); }, [editing]);

  const flagged = useMemo(() => rows.map(r => ({ ...r, _flags: deriveOrderFlags(r.fields || {}) })), [rows]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return flagged.filter(r => {
      const f = r.fields || {};
      for (const k of ['brand', 'confirmationStatus', 'article', 'collection', 'category', 'stitchCategory', 'status', 'stitchingTeam', 'fulfillmentStatus', 'deliveryStatus', 'refundStatus', 'domain']) {
        if (applied[k] && String(f[k] || '') !== applied[k]) return false;
      }
      if (applied.delay && DELAY_FLAG[applied.delay] && !r._flags[DELAY_FLAG[applied.delay]]) return false;
      // Blank-value filters (no actual issuance / receiving / OGP / IGP).
      if (applied.noIssuance && String(f.issuanceActual || '').trim()) return false;
      if (applied.noReceiving && String(f.receivingActual || '').trim()) return false;
      if (applied.noOgp && String(f.ogpNo || '').trim()) return false;
      if (applied.noIgp && String(f.igpNo || '').trim()) return false;
      const od = String(f.orderDate || '').slice(0, 10);
      if (applied.orderFrom && (!od || od < applied.orderFrom)) return false;
      if (applied.orderTo && (!od || od > applied.orderTo)) return false;
      if (s) {
        const hay = [f.orderNumber, f.clientName, f.phone, f.article, f.collection, f.trackingId, f.stitchingTeam]
          .map(x => String(x || '').toLowerCase()).join(' ');
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [flagged, search, applied]);

  // Reset to first page (and clear selection) whenever the result set changes.
  useEffect(() => { setPage(0); setSelected(new Set()); }, [applied, search]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, pageCount - 1);
  const paged = useMemo(() => filtered.slice(pageSafe * PAGE_SIZE, (pageSafe + 1) * PAGE_SIZE), [filtered, pageSafe]);

  const stats = useMemo(() => {
    let delIssuance = 0, delReceiving = 0, delDispatch = 0, delDelivery = 0, delivered = 0, fulfilled = 0;
    for (const r of flagged) {
      const fl = r._flags;
      if (fl.delayedIssuanceStage) delIssuance++;
      if (fl.delayedReceiving) delReceiving++;
      if (fl.delayedIssuance) delDispatch++;   // delayedIssuance = dispatch stage (historical key name)
      if (fl.delayedDelivery) delDelivery++;
      if (fl.delivered) delivered++;
      if (fl.fulfilled) fulfilled++;
    }
    return { total: flagged.length, delIssuance, delReceiving, delDispatch, delDelivery, delivered, fulfilled };
  }, [flagged]);

  const distinct = useCallback((key) => [...new Set(rows.map(r => String(r.fields?.[key] || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [rows]);
  const colOpts = (key) => columns.find(c => c.key === key)?.options || [];

  const startEdit = (row, col) => { if (col.readOnly) return; setEditing({ id: row._id, key: col.key }); setDraft(row.fields?.[col.key] ?? ''); };

  // Inline auto-save — optimistic, NO reload. Server echoes a wip:updated which merges.
  const saveCell = async (row, col) => {
    const value = draft; setEditing(null);
    if (String(row.fields?.[col.key] ?? '') === String(value ?? '')) return;
    const prevFields = row.fields;
    setRows(prev => prev.map(r => r._id === row._id
      ? { ...r, fields: { ...r.fields, [col.key]: value }, fieldMeta: { ...r.fieldMeta, [col.key]: { byName: 'You', at: new Date().toISOString() } } }
      : r));
    try {
      const r = await axios.patch(`${base}/${row._id}`, { workspaceUuid, fields: { [col.key]: value } }, auth());
      if (r.data?.record) setRows(prev => prev.map(x => x._id === row._id ? r.data.record : x)); // pick up recomputed SLA dates
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.message || e.message));
      setRows(prev => prev.map(r => r._id === row._id ? { ...r, fields: prevFields } : r)); // revert
    }
  };

  const addRow = async () => {
    try { const r = await axios.post(base, { workspaceUuid, fields: {}, shopDomain: shopDomain || '' }, auth()); setRows(prev => [r.data.record, ...prev]); }
    catch (e) { alert('Add failed: ' + (e.response?.data?.message || e.message)); }
  };
  const openHistory = async (row) => {
    setHistory({ row, entries: [], loading: true });
    try { const r = await axios.get(`${base}/${row._id}/history?workspaceUuid=${workspaceUuid}`, auth()); setHistory({ row, entries: r.data.history || [], loading: false }); }
    catch { setHistory({ row, entries: [], loading: false }); }
  };

  const syncDelivery = async () => {
    setSyncing(true);
    try { const r = await axios.post(`${base}/sync-delivery`, { workspaceUuid }, auth()); await load(false); alert(`PostEx sync done — ${r.data.updated || 0} row(s) updated.`); }
    catch (e) { alert('Delivery sync failed: ' + (e.response?.data?.message || e.message)); }
    setSyncing(false);
  };

  const addPerson = async () => {
    const name = newPerson.trim(); if (!name) return;
    try { const r = await axios.post(`${base}/people`, { workspaceUuid, name }, auth()); setPeople(r.data.people || []); setNewPerson(''); }
    catch (e) { alert('Add failed: ' + (e.response?.data?.message || e.message)); }
  };
  const removePerson = async (name) => {
    try { const r = await axios.delete(`${base}/people/${encodeURIComponent(name)}?workspaceUuid=${workspaceUuid}`, auth()); setPeople(r.data.people || []); }
    catch (e) { alert('Remove failed: ' + (e.response?.data?.message || e.message)); }
  };

  // The first column (Order #) is frozen on the left and carries the history
  // button; the remaining columns keep their grouped headers.
  const firstCol = columns[0];
  const restCols = useMemo(() => columns.slice(1), [columns]);
  const restGroups = useMemo(() => {
    const out = []; let cur = null;
    for (const c of restCols) { if (!cur || cur.name !== (c.group || '')) { cur = { name: c.group || '', span: 0 }; out.push(cur); } cur.span++; }
    return out;
  }, [restCols]);

  const cellClass = (row, col) => {
    const fl = row._flags || {};
    if ((col.key === 'issuanceIdeal' || col.key === 'issuanceActual') && fl.delayedIssuanceStage) return 'ob-danger';
    if ((col.key === 'receivingIdeal' || col.key === 'receivingActual') && fl.delayedReceiving) return 'ob-danger';
    if ((col.key === 'idealIssuanceDate' || col.key === 'issuanceDate') && fl.delayedIssuance) return 'ob-danger';
    if ((col.key === 'idealDeliveryDate' || col.key === 'actualDeliveryDate') && fl.delayedDelivery) return 'ob-danger';
    return '';
  };

  const renderCell = (row, col) => {
    // Computed duration columns (read-only): warehouse time + courier time.
    if (col.computed) {
      const f = row.fields || {};
      const n = col.key === 'processingDays' ? daysBetween(f.orderDate, f.issuanceDate)
        : col.key === 'transitDays' ? daysBetween(f.issuanceDate, f.actualDeliveryDate) : null;
      const tip = col.key === 'processingDays' ? 'Order date → actual dispatch (days in warehouse)' : 'Actual dispatch → actual delivery (courier time)';
      return <div className="ob-cell ob-ro" title={tip}>{n == null ? <span className="ob-empty" /> : <span>{n}d</span>}</div>;
    }
    const isEditing = editing && editing.id === row._id && editing.key === col.key;
    const val = row.fields?.[col.key];
    const meta = row.fieldMeta?.[col.key];
    const editedTitle = meta ? `${meta.byName || '—'}${meta.at ? ' • ' + fmtWhen(meta.at) : ''}` : 'Not set';
    if (isEditing) {
      if (col.type === 'select') {
        const opts = col.key === 'stitchingTeam' ? people : (col.options || []);
        return (
          <select ref={editRef} className="ob-edit" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => saveCell(row, col)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveCell(row, col); if (e.key === 'Escape') setEditing(null); }}>
            <option value="">—</option>{opts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>);
      }
      return (<input ref={editRef} className="ob-edit" type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
        value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => saveCell(row, col)}
        onKeyDown={(e) => { if (e.key === 'Enter') saveCell(row, col); if (e.key === 'Escape') setEditing(null); }} />);
    }
    let display = (val === undefined || val === null || val === '') ? '' : String(val);
    if (col.type === 'date' && display) display = fmtDate(display);
    if (col.key === 'confirmationStatus' && val) display = (val === 'Confirmed' ? '✓ ' : val === 'Rejected' ? '✗ ' : '') + val;
    const statusCls = (col.key === 'status' || col.key === 'confirmationStatus') && val ? ` ob-chip ob-chip-${String(val).replace(/\s+/g, '-').toLowerCase()}` : '';
    const showDot = meta && meta.byName && !/auto/i.test(meta.byName) && meta.byName !== 'Migrated';
    const loc = row.fields?.deliveryLocation;
    const showLoc = col.key === 'deliveryStatus' && loc;
    const title = showLoc ? `Current location: ${loc}` : editedTitle;
    return (
      <div className={`ob-cell${meta ? ' has-meta' : ''}${col.readOnly ? ' ob-ro' : ''}`} title={title} onClick={() => startEdit(row, col)}>
        {display ? <span className={statusCls.trim() || undefined}>{display}</span> : <span className="ob-empty">{col.readOnly ? '' : '＋'}</span>}
        {showLoc && <span className="ob-loc" title={`Current location: ${loc}`}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg></span>}
        {showDot && <span className="ob-dot" />}
      </div>);
  };

  const activeCount = Object.values(applied).filter(Boolean).length;
  const openFilters = () => { setDraftF(applied); setFiltersOpen(true); };
  const applyFilters = () => { setApplied(draftF); setFiltersOpen(false); };
  const clearAll = () => { setApplied(EMPTY_FILTERS); setDraftF(EMPTY_FILTERS); setSearch(''); setFiltersOpen(false); };

  // ---- Ideal Timelines settings ----
  const TL_STAGES = [['issuance', 'Issuance'], ['receiving', 'Receiving'], ['dispatch', 'Dispatch'], ['delivery', 'Delivery']];
  const openTimelines = async () => {
    setTimelinesOpen(true);
    try { const r = await axios.get(`${base}/timelines?workspaceUuid=${workspaceUuid}`, auth()); setTimelines(r.data.timelines); }
    catch { setTimelines({ unstitched: { issuance: 1, receiving: 2, dispatch: 3, delivery: 6 }, stitched: { issuance: 3, receiving: 7, dispatch: 10, delivery: 20 } }); }
  };
  const setTl = (cat, stage, val) => setTimelines(t => ({ ...t, [cat]: { ...(t?.[cat] || {}), [stage]: val } }));
  const saveTimelines = async () => {
    if (!timelines) return;
    setSavingTimelines(true);
    try {
      const r = await axios.put(`${base}/timelines`, { workspaceUuid, timelines }, auth());
      setTimelinesOpen(false); await load(false);
      alert(`Ideal timelines saved — recomputed ideal dates on ${r.data.recomputed?.updated || 0} order(s).`);
    } catch (e) { alert('Save failed: ' + (e.response?.data?.message || e.message)); }
    setSavingTimelines(false);
  };

  // ---- Bulk selection + edit ----
  const filteredIds = useMemo(() => filtered.map(r => r._id), [filtered]);
  const allSelected = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  useEffect(() => { if (selAllRef.current) selAllRef.current.indeterminate = someSelected; }, [someSelected]);
  const toggleRow = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(filteredIds)));
  const bulkColumns = useMemo(() => columns.filter(c => !c.computed), [columns]);
  const bulkCol = columns.find(c => c.key === bulkField);
  const bulkOptsFor = (c) => c.key === 'stitchingTeam' ? people : c.key === 'confirmationStatus' ? ['Confirmed', 'Rejected'] : (c.options || []);
  const applyBulk = async () => {
    if (!bulkField || !selected.size) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${base}/bulk-update`, { workspaceUuid, ids: [...selected], fields: { [bulkField]: bulkValue } }, auth());
      setBulkOpen(false); setSelected(new Set()); await load(false);
      alert(`Updated ${r.data.updated || 0} order${r.data.updated === 1 ? '' : 's'}.`);
    } catch (e) { alert('Bulk update failed: ' + (e.response?.data?.message || e.message)); }
    setBulkBusy(false);
  };

  // Human-readable list of the filters currently in effect (for the export
  // confirmation popup + the PDF header, so the user knows the export scope).
  const activeFilterList = () => {
    const bits = [];
    if (search) bits.push(`Search: "${search}"`);
    const labelMap = { brand: 'Brand', confirmationStatus: 'Status', article: 'Article', collection: 'Collection', stitchCategory: 'Stitch', status: 'Stage', stitchingTeam: 'Stitching team', fulfillmentStatus: 'Fulfillment', deliveryStatus: 'Delivery', refundStatus: 'Refund', domain: 'Domain' };
    for (const [k, l] of Object.entries(labelMap)) if (applied[k]) bits.push(`${l}: ${applied[k]}`);
    if (applied.delay && DELAY_LABEL[applied.delay]) bits.push(DELAY_LABEL[applied.delay]);
    if (applied.orderFrom) bits.push(`Order date from ${applied.orderFrom}`);
    if (applied.orderTo) bits.push(`Order date to ${applied.orderTo}`);
    if (applied.noIssuance) bits.push('No actual issuance');
    if (applied.noReceiving) bits.push('No actual receiving');
    if (applied.noOgp) bits.push('No OGP #');
    if (applied.noIgp) bits.push('No IGP #');
    return bits;
  };

  // ---- Branded PDF export — generates a real PDF file and downloads it to the
  // user's device (phone / laptop). Shows an "Exporting…" state while building. ----
  const doExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const logo = await loadLogoDataUrl();
      const brand = brandName || 'Workspace';
      const cols = [['orderNumber', 'Order #'], ['confirmationStatus', 'Status'], ['orderDate', 'Order Date'], ['clientName', 'Client'], ['phone', 'Phone'], ['article', 'Article'], ['stitchCategory', 'Stitch'], ['size', 'Size'], ['status', 'Stage'], ['idealIssuanceDate', 'Ideal Disp.'], ['issuanceDate', 'Actual Disp.'], ['idealDeliveryDate', 'Ideal Del.'], ['actualDeliveryDate', 'Actual Del.'], ['trackingId', 'Tracking'], ['deliveryStatus', 'Delivery'], ['refundStatus', 'Refund']];
      const dateCols = new Set(['orderDate', 'idealIssuanceDate', 'issuanceDate', 'idealDeliveryDate', 'actualDeliveryDate']);
      const issDangerIdx = new Set([9, 10]);   // ideal/actual issuance columns
      const delDangerIdx = new Set([11, 12]);  // ideal/actual delivery columns

      const body = filtered.map(r => { const f = r.fields || {}; return cols.map(([k]) => { let v = f[k] ?? ''; if (dateCols.has(k) && v) v = fmtDate(v); return String(v); }); });
      const metas = filtered.map(r => r._flags || {});

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();
      // Branded header — logo + wordmark
      if (logo) { try { doc.addImage(logo, 'PNG', 12, 6.5, 11, 11); } catch { /* skip */ } }
      const bx = logo ? 26 : 12;
      doc.setFontSize(17); doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42);
      doc.text('Vetobase', bx, 15);
      const bw = doc.getTextWidth('Vetobase');
      doc.setTextColor(29, 78, 216); doc.text('  ×  ', bx + bw, 15);
      const xw = doc.getTextWidth('  ×  ');
      doc.setTextColor(15, 23, 42); doc.text(brand, bx + bw + xw, 15);
      doc.setFontSize(10); doc.setTextColor(29, 78, 216); doc.text('www.vetobase.com', W - 12, 14, { align: 'right' });
      doc.setDrawColor(29, 78, 216); doc.setLineWidth(0.8); doc.line(12, 19, W - 12, 19);

      const filterBits = activeFilterList().join('  •  ') || 'All orders';
      doc.setFontSize(8.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(100, 116, 139);
      doc.text(`Order Tracking Report  —  ${filterBits}`, 12, 24);
      const delv = filtered.filter(r => r._flags.delivered).length;
      const lIss = filtered.filter(r => r._flags.delayedIssuanceStage).length, lRec = filtered.filter(r => r._flags.delayedReceiving).length;
      const lDis = filtered.filter(r => r._flags.delayedIssuance).length, lDel = filtered.filter(r => r._flags.delayedDelivery).length;
      doc.text(`Orders: ${filtered.length}    Delivered: ${delv}    Late — issuance: ${lIss}, receiving: ${lRec}, dispatch: ${lDis}, delivery: ${lDel}    Generated: ${new Date().toLocaleString()}`, 12, 29);

      autoTable(doc, {
        head: [cols.map(([, l]) => l)], body, startY: 33,
        theme: 'striped', styles: { fontSize: 6.5, cellPadding: 1.3, overflow: 'linebreak', lineColor: [230, 234, 240], lineWidth: 0.1 },
        headStyles: { fillColor: [29, 78, 216], textColor: 255, fontSize: 6.5, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 251, 252] },
        didParseCell: (d) => {
          if (d.section !== 'body') return;
          const m = metas[d.row.index] || {};
          if ((issDangerIdx.has(d.column.index) && m.delayedIssuance) || (delDangerIdx.has(d.column.index) && m.delayedDelivery)) {
            d.cell.styles.textColor = [220, 38, 38]; d.cell.styles.fillColor = [254, 242, 242]; d.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { left: 12, right: 12 },
        didDrawPage: () => {
          doc.setFontSize(7); doc.setTextColor(148, 163, 184);
          doc.text('Powered by Vetobase · www.vetobase.com', W / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
        },
      });

      const safe = brand.replace(/[^a-z0-9]+/gi, '-');
      doc.save(`Vetobase-x-${safe}-orders-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      alert('Export failed: ' + (e.message || e));
    } finally { setExporting(false); }
  };

  // ---- Excel export — real .xlsx of the filtered orders (all columns), saved to device. ----
  const doExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const cols = columns.length ? columns : [];
      const header = cols.map(c => c.label);
      const body = filtered.map(r => {
        const f = r.fields || {};
        return cols.map(c => {
          if (c.key === 'processingDays') { const n = daysBetween(f.orderDate, f.issuanceDate); return n == null ? '' : n; }
          if (c.key === 'transitDays') { const n = daysBetween(f.issuanceDate, f.actualDeliveryDate); return n == null ? '' : n; }
          const v = f[c.key];
          return v == null ? '' : v;
        });
      });
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      ws['!cols'] = header.map(h => ({ wch: Math.max(10, Math.min(42, String(h).length + 4)) }));
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: body.length, c: Math.max(0, header.length - 1) } }) };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Orders');
      const safe = (brandName || 'Store').replace(/[^a-z0-9]+/gi, '-');
      XLSX.writeFile(wb, `Vetobase-x-${safe}-orders-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      alert('Excel export failed: ' + (e.message || e));
    } finally { setExporting(false); }
  };

  const filterFields = [
    { key: 'brand', label: 'Brand', opts: distinct('brand') },
    { key: 'confirmationStatus', label: 'Status (confirm / reject)', opts: ['Confirmed', 'Rejected'] },
    { key: 'article', label: 'Article', opts: distinct('article') },
    { key: 'collection', label: 'Collection', opts: distinct('collection') },
    { key: 'stitchCategory', label: 'Stitch category', opts: colOpts('stitchCategory') },
    { key: 'status', label: 'Stage', opts: colOpts('status') },
    { key: 'stitchingTeam', label: 'Stitching team', opts: people },
    { key: 'fulfillmentStatus', label: 'Fulfillment', opts: colOpts('fulfillmentStatus') },
    { key: 'deliveryStatus', label: 'Delivery status', opts: colOpts('deliveryStatus') },
    { key: 'refundStatus', label: 'Refund / return', opts: distinct('refundStatus') },
    { key: 'domain', label: 'Domain', opts: colOpts('domain') },
  ];

  return (
    <div className="ob-wrap">
      <style>{css}</style>

      <div className="ob-stats">
        <div className="ob-stat"><b>{stats.total}</b><span>Orders</span></div>
        <div className="ob-stat ob-s-ok"><b>{stats.fulfilled}</b><span>Fulfilled</span></div>
        <div className="ob-stat ob-s-ok"><b>{stats.delivered}</b><span>Delivered</span></div>
        {[['issuance', stats.delIssuance, 'ob-s-warn'], ['receiving', stats.delReceiving, 'ob-s-warn'], ['dispatch', stats.delDispatch, 'ob-s-warn'], ['delivery', stats.delDelivery, 'ob-s-bad']].map(([stage, count, cls]) => (
          <div key={stage} className={`ob-stat ${cls} ${applied.delay === stage ? 'on' : ''}`}
            title={`Filter: ${DELAY_LABEL[stage].toLowerCase()}`}
            onClick={() => setApplied(a => ({ ...a, delay: a.delay === stage ? '' : stage }))}>
            <b>{count}</b><span>{DELAY_LABEL[stage]}</span>
          </div>
        ))}
      </div>

      <div className="ob-toolbar">
        <input className="ob-search" placeholder="Search order, client, phone, article, tracking…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button className={`ob-btn ${activeCount ? 'primary' : ''}`} onClick={openFilters}>Filters{activeCount ? ` · ${activeCount}` : ''}</button>
        {(activeCount || search) ? <button className="ob-btn ghost" onClick={clearAll}>Clear</button> : null}
        <div className="ob-tb-right">
          <span className="ob-count">{filtered.length}{(activeCount || search) ? ` / ${stats.total}` : ''}</span>
          <button className="ob-btn" onClick={() => setManagePeople(true)} title="Manage the Stitching Team list">Stitching team</button>
          <button className="ob-btn" onClick={openTimelines} title="Configure the ideal timelines (issuance / receiving / dispatch / delivery)">Ideal Timelines</button>
          <button className="ob-btn" onClick={syncDelivery} disabled={syncing}>{syncing ? 'Syncing…' : 'Sync delivery'}</button>
          <button className="ob-btn" onClick={() => setExportOpen(true)} title="Export & save the filtered orders (Excel or PDF)">Export</button>
          <button className="ob-btn" onClick={() => load(true)}>Refresh</button>
          <button className="ob-btn primary" onClick={addRow}>+ Add</button>
        </div>
      </div>

      {/* active filter chips */}
      {activeCount > 0 && (
        <div className="ob-chips">
          {Object.entries(applied).filter(([, v]) => v).map(([k, v]) => {
            const label = k === 'delay' ? (DELAY_LABEL[v] || 'Late')
              : k === 'orderFrom' ? `From ${v}` : k === 'orderTo' ? `To ${v}`
              : k === 'noIssuance' ? 'No issuance' : k === 'noReceiving' ? 'No receiving'
              : k === 'noOgp' ? 'No OGP' : k === 'noIgp' ? 'No IGP' : v;
            return <span className="ob-fchip" key={k}>{label}<button onClick={() => setApplied(a => ({ ...a, [k]: '' }))}>✕</button></span>;
          })}
        </div>
      )}

      {err && <div className="ob-err">{err}</div>}

      {loading ? (
        <SkelTable rows={10} cols={8} />
      ) : (
        <>
          <div className="ob-scroll">
            <table className="ob-table">
              <thead>
                <tr className="ob-grp-row">
                  <th className="ob-sticky ob-firstcol-h" rowSpan={2}>
                    <span className="ob-firstcol-h-inner">
                      <input ref={selAllRef} type="checkbox" className="ob-selall" checked={allSelected} onChange={toggleAll} title="Select all in view" />
                      {firstCol?.label || 'Order #'}
                    </span>
                  </th>
                  {restGroups.map((g, i) => <th key={i} colSpan={g.span} className={`ob-grp ob-grp-${g.name.toLowerCase()}`}>{g.name}</th>)}
                </tr>
                <tr>{restCols.map(c => <th key={c.key} className={c.auto ? 'ob-auto-h' : ''} title={c.synced ? 'Auto-synced from Shopify/PostEx' : c.auto ? 'Auto-filled from Shopify' : 'Manual'}>{c.label}{c.auto && <span className="ob-auto-tag">{c.synced ? 'sync' : 'auto'}</span>}</th>)}</tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr><td className="ob-none" colSpan={columns.length}>{(activeCount || search) ? 'No orders match these filters.' : 'No orders yet. New Shopify orders appear here automatically.'}</td></tr>
                ) : paged.map(row => (
                  <tr key={row._id} className={`${row._flags.delayedDelivery ? 'ob-row-bad' : row._flags.delayedIssuance ? 'ob-row-warn' : ''}${selected.has(row._id) ? ' ob-row-sel' : ''}`}>
                    <td className="ob-sticky ob-firstcol">
                      <input type="checkbox" className="ob-rowcheck" checked={selected.has(row._id)} onChange={(e) => { e.stopPropagation(); toggleRow(row._id); }} title="Select order" />
                      <button className="ob-hist-btn" title="Change history" onClick={() => openHistory(row)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></svg></button>
                      {firstCol && <div className="ob-firstcol-val">{renderCell(row, firstCol)}</div>}
                    </td>
                    {restCols.map(c => <td key={c.key} className={cellClass(row, c)}>{renderCell(row, c)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="ob-pager">
              <span className="ob-pg-info">{pageSafe * PAGE_SIZE + 1}–{Math.min((pageSafe + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
              <div className="ob-pg-btns">
                <button className="ob-pg" disabled={pageSafe === 0} onClick={() => setPage(0)}>«</button>
                <button className="ob-pg" disabled={pageSafe === 0} onClick={() => setPage(pageSafe - 1)}>‹</button>
                <span className="ob-pg-cur">Page {pageSafe + 1} / {pageCount}</span>
                <button className="ob-pg" disabled={pageSafe >= pageCount - 1} onClick={() => setPage(pageSafe + 1)}>›</button>
                <button className="ob-pg" disabled={pageSafe >= pageCount - 1} onClick={() => setPage(pageCount - 1)}>»</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Floating bulk-action bar — appears when one or more orders are selected */}
      {selected.size > 0 && (
        <div className="ob-bulkbar">
          <span className="ob-bulkbar-count"><b>{selected.size}</b> selected</span>
          <button className="ob-bulkbar-edit" onClick={() => { setBulkField(''); setBulkValue(''); setBulkOpen(true); }}>✎ Bulk edit</button>
          <button className="ob-bulkbar-clear" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {/* Bulk-edit modal — pick one column, set one value, apply to every selected order */}
      {bulkOpen && (
        <div className="ob-scrim" onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div className="ob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ob-drawer-head"><h3>Bulk edit {selected.size} order{selected.size === 1 ? '' : 's'}</h3><button onClick={() => !bulkBusy && setBulkOpen(false)}>✕</button></div>
            <div style={{ padding: '16px 20px' }}>
              <label className="ob-bulk-lbl">Column to change</label>
              <select className="ob-bulk-input" value={bulkField} onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); }}>
                <option value="">— Choose a column —</option>
                {bulkColumns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>

              {bulkCol && (
                <div style={{ marginTop: 14 }}>
                  <label className="ob-bulk-lbl">New value for “{bulkCol.label}”</label>
                  {bulkOptsFor(bulkCol).length > 0 ? (
                    <select className="ob-bulk-input" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                      <option value="">— (clear / empty) —</option>
                      {bulkOptsFor(bulkCol).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input className="ob-bulk-input" type={bulkCol.type === 'number' ? 'number' : bulkCol.type === 'date' ? 'date' : 'text'}
                      value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Leave blank to clear the value" />
                  )}
                  <p className="ob-bulk-note">This sets <b>{bulkCol.label}</b> to the same value on all {selected.size} selected order{selected.size === 1 ? '' : 's'}. Leaving it blank clears the field.</p>
                </div>
              )}
            </div>
            <div className="ob-factions">
              <button className="ob-btn ghost" onClick={() => !bulkBusy && setBulkOpen(false)} disabled={bulkBusy}>Cancel</button>
              <button className="ob-btn primary" onClick={applyBulk} disabled={bulkBusy || !bulkField}>{bulkBusy ? 'Applying…' : `Apply to ${selected.size}`}</button>
            </div>
          </div>
        </div>
      )}

      {/* Export confirmation — shows exactly what will be exported before generating */}
      {exportOpen && (
        <div className="ob-scrim" onClick={() => !exporting && setExportOpen(false)}>
          <div className="ob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ob-drawer-head"><h3>Export orders</h3><button onClick={() => !exporting && setExportOpen(false)}>✕</button></div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#3A3A37' }}>
                <b>{filtered.length}</b> order{filtered.length === 1 ? '' : 's'} will be exported and saved to your device — <b>Excel</b> (all columns) or a branded <b>Vetobase × {brandName || 'Store'}</b> <b>PDF</b>.
              </p>
              <div className="ob-exp-lbl">Filters applied to this export</div>
              <div className="ob-exp-chips">
                {activeFilterList().length === 0
                  ? <span className="ob-fchip ob-fchip-plain">All orders — no filters</span>
                  : activeFilterList().map((b, i) => <span className="ob-fchip ob-fchip-plain" key={i}>{b}</span>)}
              </div>
              {filtered.length === 0 && <p style={{ margin: '12px 0 0', fontSize: 12, color: '#B23A3A', fontWeight: 600 }}>No orders match — adjust filters before exporting.</p>}
            </div>
            <div className="ob-factions">
              <button className="ob-btn ghost" onClick={() => setExportOpen(false)} disabled={exporting}>Cancel</button>
              <button className="ob-btn" disabled={exporting || filtered.length === 0}
                onClick={async () => { await doExportExcel(); setExportOpen(false); }}>{exporting ? 'Exporting…' : 'Excel (.xlsx)'}</button>
              <button className="ob-btn primary" disabled={exporting || filtered.length === 0}
                onClick={async () => { await doExport(); setExportOpen(false); }}>{exporting ? 'Exporting…' : 'PDF'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Filters popup */}
      {filtersOpen && (
        <div className="ob-scrim" onClick={() => setFiltersOpen(false)}>
          <div className="ob-modal ob-fmodal" onClick={(e) => e.stopPropagation()}>
            <div className="ob-drawer-head"><h3>Filter orders</h3><button onClick={() => setFiltersOpen(false)}>✕</button></div>
            <div className="ob-fgrid">
              {filterFields.map(ff => (
                <label className="ob-field" key={ff.key}>
                  <span>{ff.label}</span>
                  <select value={draftF[ff.key]} onChange={(e) => setDraftF(d => ({ ...d, [ff.key]: e.target.value }))}>
                    <option value="">All</option>
                    {ff.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ))}
              <label className="ob-field">
                <span>Late stage</span>
                <select value={draftF.delay} onChange={(e) => setDraftF(d => ({ ...d, delay: e.target.value }))}>
                  <option value="">Any</option>
                  <option value="issuance">Late issuance</option>
                  <option value="receiving">Late receiving</option>
                  <option value="dispatch">Late dispatch</option>
                  <option value="delivery">Late delivery</option>
                </select>
              </label>
              <label className="ob-field"><span>Order date from</span><input type="date" value={draftF.orderFrom} onChange={(e) => setDraftF(d => ({ ...d, orderFrom: e.target.value }))} /></label>
              <label className="ob-field"><span>Order date to</span><input type="date" value={draftF.orderTo} onChange={(e) => setDraftF(d => ({ ...d, orderTo: e.target.value }))} /></label>
              <label className="ob-field"><span>Issuance</span><select value={draftF.noIssuance} onChange={(e) => setDraftF(d => ({ ...d, noIssuance: e.target.value }))}><option value="">Any</option><option value="yes">No actual issuance</option></select></label>
              <label className="ob-field"><span>Receiving</span><select value={draftF.noReceiving} onChange={(e) => setDraftF(d => ({ ...d, noReceiving: e.target.value }))}><option value="">Any</option><option value="yes">No actual receiving</option></select></label>
              <label className="ob-field"><span>OGP #</span><select value={draftF.noOgp} onChange={(e) => setDraftF(d => ({ ...d, noOgp: e.target.value }))}><option value="">Any</option><option value="yes">No OGP #</option></select></label>
              <label className="ob-field"><span>IGP #</span><select value={draftF.noIgp} onChange={(e) => setDraftF(d => ({ ...d, noIgp: e.target.value }))}><option value="">Any</option><option value="yes">No IGP #</option></select></label>
            </div>
            <div className="ob-factions">
              <button className="ob-btn ghost" onClick={() => setDraftF(EMPTY_FILTERS)}>Reset</button>
              <button className="ob-btn primary" onClick={applyFilters}>Apply &amp; search</button>
            </div>
          </div>
        </div>
      )}

      {history && (
        <div className="ob-scrim" onClick={() => setHistory(null)}>
          <div className="ob-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="ob-drawer-head"><h3>Change history</h3><button onClick={() => setHistory(null)}>✕</button></div>
            {history.loading ? <div className="ob-loading"><div className="ob-spin" /></div>
              : history.entries.length === 0 ? <div className="ob-none">No history.</div>
                : <div className="ob-hist">{history.entries.map((h, i) => (
                  <div className="ob-hist-row" key={i}>
                    <div className="ob-hist-top"><b>{h.byUserName || '—'}</b><span className="ob-hist-when">{fmtWhen(h.at)}</span></div>
                    <div className="ob-hist-body">{h.action === 'create' ? 'created this row' : h.action === 'delete' ? 'deleted this row'
                      : <>changed <b>{h.fieldLabel || h.field}</b> {h.oldValue ? <>from “<span className="ob-old">{h.oldValue}</span>” </> : ''}to “<span className="ob-new">{h.newValue || '—'}</span>”</>}</div>
                  </div>))}</div>}
          </div>
        </div>
      )}

      {timelinesOpen && (
        <div className="ob-scrim" onClick={() => !savingTimelines && setTimelinesOpen(false)}>
          <div className="ob-modal ob-fmodal" onClick={(e) => e.stopPropagation()}>
            <div className="ob-drawer-head"><h3>Ideal Timelines</h3><button onClick={() => !savingTimelines && setTimelinesOpen(false)}>✕</button></div>
            <p className="ob-modal-sub">Days from order date for each stage — these drive the ideal dates. Saving recomputes ideal dates on all orders (your actual/manual dates are never touched).</p>
            {!timelines ? <div className="ob-loading"><div className="ob-spin" /></div> : (
              <div style={{ padding: '12px 20px' }}>
                <table className="ob-tl">
                  <thead><tr><th>Stage</th><th>Unstitched</th><th>Stitched</th></tr></thead>
                  <tbody>
                    {TL_STAGES.map(([key, label]) => (
                      <tr key={key}>
                        <td className="ob-tl-label">{label}</td>
                        <td><input type="number" min="0" value={timelines.unstitched?.[key] ?? ''} onChange={(e) => setTl('unstitched', key, e.target.value)} /><span>days</span></td>
                        <td><input type="number" min="0" value={timelines.stitched?.[key] ?? ''} onChange={(e) => setTl('stitched', key, e.target.value)} /><span>days</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="ob-factions">
              <button className="ob-btn ghost" onClick={() => setTimelinesOpen(false)} disabled={savingTimelines}>Cancel</button>
              <button className="ob-btn primary" onClick={saveTimelines} disabled={savingTimelines || !timelines}>{savingTimelines ? 'Saving…' : 'Save & recompute'}</button>
            </div>
          </div>
        </div>
      )}

      {managePeople && (
        <div className="ob-scrim" onClick={() => setManagePeople(false)}>
          <div className="ob-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ob-drawer-head"><h3>Stitching Team</h3><button onClick={() => setManagePeople(false)}>✕</button></div>
            <p className="ob-modal-sub">These names fill the “Stitching Team” dropdown on every order row.</p>
            <div className="ob-people">
              {people.length === 0 ? <div className="ob-none">No people yet — add the first below.</div>
                : people.map(p => <div key={p} className="ob-person"><span>{p}</span><button onClick={() => removePerson(p)} title="Remove">✕</button></div>)}
            </div>
            <div className="ob-add-person">
              <input value={newPerson} placeholder="Add a person…" onChange={(e) => setNewPerson(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addPerson(); }} />
              <button className="ob-btn primary" onClick={addPerson}>+ Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.ob-wrap{ font-size:13px; }
.ob-stats{ display:flex; gap:8px; margin-bottom:10px; flex-wrap:wrap; }
.ob-stat{ background:#FFFFFF; border:1px solid #E7E1D6; border-radius:14px; padding:6px 13px; min-width:84px; }
.ob-stat b{ display:block; font-size:18px; font-weight:700; color:#111111; line-height:1.15; font-family:'IBM Plex Mono',monospace; }
.ob-stat span{ font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.03em; color:#9C978C; }
.ob-s-ok b{ color:#0F6E56; } .ob-s-warn b{ color:#8A5A12; } .ob-s-bad b{ color:#B23A3A; }
.ob-s-warn, .ob-s-bad{ cursor:pointer; transition:box-shadow .15s, transform .1s; }
.ob-s-warn:hover, .ob-s-bad:hover{ transform:translateY(-1px); }
.ob-s-warn.on{ outline:2px solid #8A5A12; } .ob-s-bad.on{ outline:2px solid #B23A3A; }

.ob-toolbar{ display:flex; align-items:center; gap:8px; margin-bottom:12px; flex-wrap:wrap; }
.ob-search{ flex:1; min-width:240px; padding:9px 13px; border:1px solid #E7E1D6; border-radius:999px; font-size:13px; outline:none; }
.ob-search:focus{ border-color:#111111; }
.ob-tb-right{ display:flex; align-items:center; gap:7px; margin-left:auto; flex-wrap:wrap; }
.ob-count{ font-size:12.5px; font-weight:700; color:#6B6862; font-family:'IBM Plex Mono',monospace; }
.ob-btn{ background:#FFFFFF; border:1px solid #E7E1D6; color:#3A3A37; font-size:12.5px; font-weight:700; padding:8px 13px; border-radius:999px; cursor:pointer; transition:background .12s, border-color .12s; }
.ob-btn:hover{ background:#FAF7F1; }
.ob-btn.primary{ background:#111111; border-color:#111111; color:#F9F4EE; box-shadow:4px 4px 0 rgba(17,17,17,.2); } .ob-btn.primary:hover{ background:#000000; }
.ob-btn.ghost{ border-style:dashed; color:#6B6862; } .ob-btn:disabled{ opacity:.5; cursor:default; }
.ob-err{ font-size:13px; color:#8A2B16; background:#FBEDE9; border:1px solid #E7E1D6; padding:10px 14px; border-radius:14px; margin-bottom:14px; font-weight:600; }

.ob-chips{ display:flex; gap:6px; flex-wrap:wrap; margin-bottom:12px; }
.ob-fchip{ display:inline-flex; align-items:center; gap:6px; background:#FAF7F1; color:#3A3A37; border:1px solid #E7E1D6; border-radius:999px; padding:3px 6px 3px 11px; font-size:11.5px; font-weight:700; }
.ob-fchip button{ background:#E7E1D6; border:none; color:#3A3A37; border-radius:50%; width:16px; height:16px; cursor:pointer; font-size:10px; line-height:1; }

.ob-scroll{ overflow:auto; max-height:calc(100vh - 250px); border:1px solid #E7E1D6; border-radius:14px; background:#FFFFFF; }
.ob-table{ border-collapse:separate; border-spacing:0; min-width:1500px; width:100%; }
.ob-table th, .ob-table td{ border-bottom:1px solid #E7E1D6; border-right:1px solid #E7E1D6; }
.ob-table thead th{ position:sticky; background:#FAF7F1; z-index:4; font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.03em; color:#9C978C; padding:4px 8px; text-align:left; white-space:nowrap; }
.ob-table thead tr:first-child th{ top:0; } .ob-table thead tr:nth-child(2) th{ top:20px; }
.ob-grp{ text-align:center !important; font-size:10px !important; color:#9C978C; background:#FAF7F1 !important; border-bottom:1px solid #E7E1D6; }
.ob-grp-order{ color:#6B6862; } .ob-grp-product{ color:#6B6862; } .ob-grp-processing{ color:#6B6862; } .ob-grp-dates{ color:#6B6862; } .ob-grp-fulfillment{ color:#6B6862; } .ob-grp-docs{ color:#6B6862; }
.ob-auto-h{ color:#9C978C; }
.ob-auto-tag{ display:inline-block; margin-left:5px; font-size:8.5px; font-weight:800; background:#FAF7F1; color:#6B6862; padding:1px 5px; border-radius:999px; vertical-align:middle; letter-spacing:.03em; font-family:'IBM Plex Mono',monospace; }
.ob-table tbody td{ padding:0; font-size:11.5px; color:#3A3A37; vertical-align:middle; font-family:'IBM Plex Mono',monospace; }
.ob-table tbody tr:hover{ background:#FAF7F1; }
.ob-row-warn{ background:#F5EAD5; } .ob-row-bad{ background:#F6E4E1; }
.ob-row-warn:hover{ background:#EFDFC2; } .ob-row-bad:hover{ background:#F1DAD4; }

.ob-cell{ padding:4px 8px; min-height:16px; cursor:text; min-width:58px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:200px; }
.ob-cell.has-meta{ cursor:pointer; }
.ob-empty{ color:#9C978C; } .ob-dot{ display:inline-block; width:5px; height:5px; border-radius:50%; background:#111111; margin-left:5px; vertical-align:middle; }
.ob-ro{ cursor:default; color:#3A3A37; } .ob-ro .ob-empty{ color:#E7E1D6; }
.ob-loc{ margin-left:5px; font-size:10px; cursor:help; vertical-align:middle; color:#2A5B8A; }
.ob-danger .ob-cell span:first-child{ color:#B23A3A; font-weight:800; }
.ob-danger{ background:rgba(178,58,58,.08); }
.ob-edit{ width:100%; box-sizing:border-box; padding:4px 6px; border:2px solid #111111; border-radius:5px; font-size:11.5px; outline:none; }

.ob-chip{ display:inline-block; padding:1px 8px; border-radius:999px; font-size:10px; font-weight:700; background:#FAF7F1; color:#3A3A37; }
.ob-chip-delivered{ background:#E4F1E9; color:#0F6E56; } .ob-chip-dispatched{ background:#E1ECF5; color:#2A5B8A; }
.ob-chip-cancelled{ background:#F6E4E1; color:#B23A3A; } .ob-chip-qc{ background:#F5EAD5; color:#8A5A12; }
.ob-chip-new{ background:#F6E4E1; color:#B23A3A; } .ob-chip-in-store{ background:#E1ECF5; color:#2A5B8A; }
.ob-chip-missing-components{ background:#F6E4E1; color:#B23A3A; }
.ob-chip-confirmed{ background:#E4F1E9; color:#0F6E56; } .ob-chip-rejected{ background:#F6E4E1; color:#B23A3A; }

.ob-sticky{ position:sticky; left:0; z-index:3; background:#FFFFFF; }
thead .ob-sticky{ z-index:6; background:#FAF7F1; }   /* corner cell sits above everything */
.ob-row-warn .ob-sticky{ background:#F5EAD5; } .ob-row-bad .ob-sticky{ background:#F6E4E1; }
/* One continuous thin black boundary around the whole Order # column (header + data). */
/* z-index:7 (more specific than .ob-table thead th) keeps this corner cell above
   every other header, so the other column headers slide BEHIND it when scrolling. */
.ob-table thead th.ob-firstcol-h{ min-width:104px; border:1px solid #111111; border-bottom:none; z-index:7; border-top-left-radius:11px; }
.ob-table td.ob-firstcol{ white-space:nowrap; padding-left:3px; border-left:1px solid #111111; border-right:1px solid #111111; }
.ob-table tbody tr:last-child td.ob-firstcol{ border-bottom:1px solid #111111; border-bottom-left-radius:11px; }
.ob-hist-btn{ background:none; border:none; cursor:pointer; color:#9C978C; opacity:.75; padding:2px 3px; border-radius:999px; vertical-align:middle; display:inline-flex; }
.ob-hist-btn:hover{ opacity:1; color:#111111; background:#FAF7F1; }
.ob-firstcol-val{ display:inline-block; vertical-align:middle; }
.ob-none{ text-align:center; color:#9C978C; padding:34px; font-weight:600; }

/* Bulk multi-select */
.ob-firstcol-h-inner{ display:inline-flex; align-items:center; gap:6px; }
.ob-selall, .ob-rowcheck{ width:15px; height:15px; cursor:pointer; accent-color:#111111; vertical-align:middle; margin:0; flex:none; }
.ob-rowcheck{ margin-right:2px; }
.ob-row-sel .ob-sticky{ background:#F1ECE2; }
.ob-row-sel td{ background:#F1ECE2; }
.ob-bulkbar{ position:fixed; left:50%; bottom:22px; transform:translateX(-50%); z-index:1200; display:flex; align-items:center; gap:12px; background:#111111; color:#F9F4EE; padding:10px 12px 10px 18px; border-radius:14px; box-shadow:4px 4px 0 rgba(17,17,17,.2); animation:obBarUp .18s ease; }
@keyframes obBarUp{ from{ opacity:0; transform:translate(-50%,10px); } to{ opacity:1; transform:translate(-50%,0); } }
.ob-bulkbar-count{ font-size:13px; font-weight:600; color:rgba(249,244,238,.65); } .ob-bulkbar-count b{ color:#F9F4EE; font-weight:800; font-family:'IBM Plex Mono',monospace; }
.ob-bulkbar-edit{ background:#F9F4EE; border:none; color:#111111; font-size:13px; font-weight:700; padding:8px 15px; border-radius:999px; cursor:pointer; }
.ob-bulkbar-edit:hover{ background:#FFFFFF; }
.ob-bulkbar-clear{ background:transparent; border:none; color:rgba(249,244,238,.6); font-size:13px; font-weight:700; padding:8px 10px; border-radius:999px; cursor:pointer; }
.ob-bulkbar-clear:hover{ color:#F9F4EE; background:rgba(249,244,238,.12); }
.ob-bulk-lbl{ display:block; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#9C978C; margin-bottom:6px; }
.ob-bulk-input{ width:100%; padding:10px 12px; border:1px solid #E7E1D6; border-radius:999px; font-size:13px; font-weight:600; color:#3A3A37; background:#FFFFFF; outline:none; box-sizing:border-box; }
.ob-bulk-input:focus{ border-color:#111111; }
.ob-bulk-note{ margin:10px 0 0; font-size:12px; color:#6B6862; line-height:1.5; }

.ob-pager{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 4px 2px; flex-wrap:wrap; }
.ob-pg-info{ font-size:12.5px; font-weight:700; color:#6B6862; font-family:'IBM Plex Mono',monospace; }
.ob-pg-btns{ display:flex; align-items:center; gap:5px; }
.ob-pg{ background:#FFFFFF; border:1px solid #E7E1D6; color:#3A3A37; font-size:13px; font-weight:700; padding:6px 11px; border-radius:999px; cursor:pointer; min-width:34px; }
.ob-pg:hover:not(:disabled){ background:#FAF7F1; border-color:#E7E1D6; color:#111111; } .ob-pg:disabled{ opacity:.45; cursor:default; }
.ob-pg-cur{ font-size:12.5px; font-weight:700; color:#3A3A37; padding:0 6px; font-family:'IBM Plex Mono',monospace; }

.ob-loading{ display:flex; justify-content:center; padding:44px; }
.ob-spin{ width:26px; height:26px; border:3px solid #E7E1D6; border-top-color:#111111; border-radius:50%; animation:obSpin .7s linear infinite; }
@keyframes obSpin{ to{ transform:rotate(360deg); } }

.ob-scrim{ position:fixed; inset:0; background:rgba(17,17,17,.3); z-index:1000; display:flex; justify-content:center; align-items:flex-start; }
.ob-drawer{ margin-left:auto; width:420px; max-width:92vw; background:#FFFFFF; height:100%; overflow-y:auto; border-left:1px solid #E7E1D6; box-shadow:-4px 4px 0 rgba(17,17,17,.2); }
.ob-modal{ margin-top:6vh; width:440px; max-width:92vw; max-height:84vh; overflow-y:auto; background:#FFFFFF; border:1px solid #E7E1D6; border-radius:20px; box-shadow:4px 4px 0 rgba(17,17,17,.2); }
.ob-fmodal{ width:640px; }
.ob-drawer-head{ display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #E7E1D6; position:sticky; top:0; background:#FFFFFF; }
.ob-drawer-head h3{ margin:0; font-size:15px; font-weight:700; letter-spacing:-0.03em; color:#111111; }
.ob-drawer-head button{ background:none; border:none; font-size:16px; cursor:pointer; color:#9C978C; }
.ob-fgrid{ display:grid; grid-template-columns:repeat(2,1fr); gap:12px 14px; padding:18px 20px; }
.ob-field{ display:flex; flex-direction:column; gap:5px; }
.ob-field span{ font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#9C978C; }
.ob-field select, .ob-field input{ padding:9px 11px; border:1px solid #E7E1D6; border-radius:999px; font-size:13px; font-weight:600; color:#3A3A37; background:#FFFFFF; cursor:pointer; outline:none; }
.ob-field select:focus, .ob-field input:focus{ border-color:#111111; }
.ob-factions{ display:flex; justify-content:flex-end; gap:8px; padding:6px 20px 20px; }
.ob-exp-lbl{ font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#9C978C; margin-bottom:8px; }
.ob-exp-chips{ display:flex; flex-wrap:wrap; gap:6px; }
.ob-fchip-plain{ padding:4px 11px; }
.ob-tl{ width:100%; border-collapse:collapse; }
.ob-tl th{ text-align:left; font-size:10.5px; text-transform:uppercase; letter-spacing:.04em; color:#9C978C; padding:6px 8px; border-bottom:1px solid #E7E1D6; }
.ob-tl td{ padding:8px; border-bottom:1px solid #E7E1D6; font-size:13px; }
.ob-tl-label{ font-weight:700; color:#3A3A37; }
.ob-tl input{ width:60px; padding:6px 8px; border:1px solid #E7E1D6; border-radius:999px; font-size:13px; font-family:'IBM Plex Mono',monospace; }
.ob-tl input:focus{ border-color:#111111; outline:none; }
.ob-tl td span{ color:#9C978C; font-size:11px; margin-left:5px; }
.ob-modal-sub{ padding:14px 20px 0; margin:0; color:#6B6862; font-size:12.5px; }
.ob-people{ padding:14px 20px; display:flex; flex-direction:column; gap:7px; }
.ob-person{ display:flex; align-items:center; justify-content:space-between; padding:8px 12px; background:#FAF7F1; border:1px solid #E7E1D6; border-radius:9px; font-weight:600; color:#3A3A37; }
.ob-person button{ background:none; border:none; color:#9C978C; cursor:pointer; font-size:13px; } .ob-person button:hover{ color:#B23A3A; }
.ob-add-person{ display:flex; gap:8px; padding:6px 20px 20px; }
.ob-add-person input{ flex:1; padding:9px 12px; border:1px solid #E7E1D6; border-radius:999px; font-size:13px; outline:none; }
.ob-add-person input:focus{ border-color:#111111; }

.ob-hist{ padding:8px 0; } .ob-hist-row{ padding:11px 20px; border-bottom:1px solid #E7E1D6; }
.ob-hist-top{ display:flex; justify-content:space-between; margin-bottom:3px; } .ob-hist-top b{ font-size:12.5px; color:#111111; }
.ob-hist-when{ font-size:11px; color:#9C978C; font-family:'IBM Plex Mono',monospace; } .ob-hist-body{ font-size:12.5px; color:#3A3A37; }
.ob-old{ color:#B23A3A; } .ob-new{ color:#0F6E56; font-weight:700; }
`;
